import PieChartViz from '../vizs/PieChartViz'
import BaseComponent from './BaseComponent'
/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 *
 * A pie chart is a circular statistical graphic, which is divided into slices to illustrate numerical proportion. In a pie chart, the arc length of each slice is proportional to the quantity it represents.
 *
 * ### Example Usage
 * ```javascript
 *
 *   import {PieChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new PieChart($el, data, config);
 * ```
 *
 * This class is only intended to draw simple Pie charts. For regular [Donut Chart](#/developer/DonutChartComponent)s see the corresponding documentation.
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 2 columns. Each row represents a sector: the first column should be the id/label of the sector, and the second column its value.
 *
 * | category | value  |
 * | -------- | ------ |
 * |   a      |    22.4|
 * |   b      |    52.3|
 * |   c      |    12.8|
 * |   d      |    26.0|
 *
 * {.data-table}
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 *
 * Un gráfico circular es un gráfico estadístico circular, que se divide en porciones para ilustrar la proporción numérica. En un gráfico circular, la longitud del arco de cada sector es proporcional a la cantidad que representa.
 *
 * ### Ejemplo de uso
 * ```javascript
 *
 *   import {PieChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new PieChart($el, data, config);
 * ```
 *
 * Esta clase sólo está destinada a dibujar gráficos circulares simples. Para uso regular [Gráfico de anillo](#/developer/DonutChartComponent) ver la documentación correspondiente.
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 2 columnas. Cada fila representa un sector: la primera columna debe ser el id/etiqueta del sector y la segunda columna su valor.
 *
 * | categoría | valor  |
 * | -------- | ------ |
 * |   a      |    22.4|
 * |   b      |    52.3|
 * |   c      |    12.8|
 * |   d      |    26.0|
 *
 * {.data-table}
 * */
export default class PieChartComponent extends BaseComponent {
  static vizClass = PieChartViz
  static componentLabel = 'Pie chart'
  static componentLabelEs = 'Gráfico circular'
  static dataFormat = [
    { index: 0, axis: '', types: [String], role: 'id', roleLabel: 'Category' },
    { index: 1, axis: '', types: [Number], role: 'value', roleLabel: 'Value' },
  ]
  static defaultConfig = {
    title: this.componentLabel,
    mode: 'pie',
    centerValue: {
      nonEditable: true,
    },
    selectedItemCenterValue: {
      nonEditable: true,
    },
    centerTopText: {
      nonEditable: true,
    },
    centerBottomText: {
      nonEditable: true,
    },
    donutThickness: {
      nonEditable: true,
    },
    TooltipBehavior: {
      tooltipValueFormat: {
        exposed: true,
        nonEditable: false,
      },
    },
  }
  static dataRequirements = {
    recomendedMaxRows: 10,
    maxRows: 30,
    sign: 'positive', //'negative','any','both'
    avoidDates: true,
    requirementsStrength: 10,
  }

  static accessibilityRulesTotalItems = Object.values(super.accessibilityRules).length
  static accessibilityRules = {
    //Use smaller values for >= 8 categories
    ...super.accessibilityRules,
    groupSmallValues: {
      id: 'groupSmallValues',
      order: this.accessibilityRulesTotalItems + 1,
      label: 'rule_group_small_values_label',
      description: 'rule_group_small_max_8_values_desc',
      sidebarFieldName: 'groupOthersThreshold',
      check: (config, data) => {
        return data.length <= 8
      },
      applyAutoConfigChange: function (config, data) {
        if (this.check(config, data)) return {}
        let newConfig = {}
        const sortedData = [...data]
        sortedData.sort((a, b) => a.value - b.value)
        const lastExtraItemIndex = sortedData.length - 8
        const groupOthersThresholdValue = sortedData[lastExtraItemIndex]?.data.percent
        if (groupOthersThresholdValue)
          newConfig = {
            ...newConfig,
            groupOthersThreshold: groupOthersThresholdValue,
          }
        return {
          oldConfig: {
            groupOthersThreshold: config.groupOthersThreshold,
          },
          newConfig: newConfig,
        }
      },
    },
    showLabels: {
      id: 'showLabels',
      order: this.accessibilityRulesTotalItems + 2,
      label: 'rule_show_labels_label',
      description: 'rule_show_labels_desc',
      sidebarFieldName: 'showElementLabel',
      check: (config, data) => {
        return config.showLabels && config.showLabelCategories
      },
      applyAutoConfigChange: function (config, data) {
        if (this.check(config, data)) return {}
        let newConfig = {
          showLabels: true,
          showLabelCategories: true,
        }
        return {
          oldConfig: {
            showLabels: config.showLabels,
            showLabelCategories: config.showLabelCategories,
          },
          newConfig: newConfig,
        }
      },
      conditionalRules: ['textures'],
    },
    textures: {
      ...super.accessibilityRules.textures,
      order: this.accessibilityRulesTotalItems + 3,
      conditionalRules: ['showLabels'],
    },
  }
}
